// Shell do seller (`spec/17-frontend.md` §8.1) no design Zenith (WEB-100): sidebar clara de
// 240px flutuando sobre a superfície do app, com grupos de nav, "Recebido no mês" e menu da
// conta no rodapé; no celular, pill de navegação fixa e folha "Mais". O desenho vem do
// canvas `product:leztypay-zenith` (`components/Shell.tsx`).
//
// O layout checa **só** `get-session` (a única rota que não passa por `auth-session`) pra
// decidir se RENDERIZA o shell: checar `/v1/me` aqui pra essa decisão faria `/app/terms`
// se redirecionar para si mesma para sempre. Gates de domínio (`terms_not_accepted`,
// `mfa_required`) chegam como `403` de QUALQUER chamada guardada e são roteados pelo
// `QueryCache` global (`app/guards.tsx`, `handleGateError`), não por este componente.
//
// `MonthRevenue`/badges (WEB-50B) chamam rotas com esse mesmo gate de termos (`GET
// /v1/me`, `GET /v1/me/counters`): em `/app/terms` elas tomam `403 terms_not_accepted` e
// o guard global navega de volta pra cá, sem laço, porque é a MESMA rota (navigate
// idempotente), testado em `e2e/shell.spec.ts`.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  Navigate,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { api } from '../api/client.ts';
import { balanceQueryOptions } from '../api/queries/balance.ts';
import { profileQueryOptions } from '../api/queries/overview.ts';
import { summaryQueryOptions } from '../api/queries/reports.ts';
import { countersQueryOptions, sellerBadges } from '../api/queries/shell.ts';
import { sessionQueryOptions } from '../api/session.ts';
import { useLiveEvents } from '../app/live-events.ts';
import { useMode } from '../app/mode.tsx';
import { filterNav } from '../app/nav.ts';
import { sellerNav } from '../app/nav-seller.ts';
import { ModeToggle } from '../components/app/mode-toggle.tsx';
import type { AccountLink } from '../components/app/shell/conta-menu.tsx';
import { AppShell } from '../components/app/shell/shell.tsx';
import { RevenueBar } from '../components/app/shell/sidebar.tsx';
import { CardSkeleton } from '../components/app/skeletons.tsx';
import { Lock, Store } from '../components/icons/index.tsx';
import { withdrawableAcquirers } from '../features/international-withdrawals/model.ts';
import { todayIsoDate } from '../lib/dates.ts';
import { formatCents } from '../lib/money.ts';

/** Atalhos da conta no menu do rodapé da nav. */
const ACCOUNT_LINKS: AccountLink[] = [
  { label: 'Loja', to: '/app/settings', icon: Store },
  { label: 'Trocar senha', to: '/app/settings/security', icon: Lock },
];

/** Os quatro atalhos da pill do celular (`spec/24` §4 item 1, desenho do canvas). */
const MOBILE_TABS = ['overview', 'transactions', 'balance', 'withdrawals'];

/** Primeiro dia do mês corrente em `America/Sao_Paulo`, como `YYYY-MM-DD`. */
function monthToDate(): { from: string; to: string } {
  const to = todayIsoDate();
  return { from: `${to.slice(0, 7)}-01`, to };
}

/**
 * "Recebido no mês" do croqui (`spec/24` §3): `sales.gross_cents` do summary do mês
 * corrente. A META (`revenue_goal_cents` de `GET /v1/me`) liga aqui (WEB-50B, "Questões
 * abertas" 6 da WEB-50): sem meta cadastrada a barra some e o card mostra só o valor.
 */
function MonthRevenue() {
  const { mode } = useMode();
  const summary = useQuery(summaryQueryOptions(mode, monthToDate()));
  const profile = useQuery(profileQueryOptions(mode));
  const receivedCents = summary.data?.sales.gross_cents ?? 0;
  const goalCents = profile.data?.revenue_goal_cents ?? null;
  // Meta ZERO também não desenha barra: dividir por ela daria `Infinity`, que o clamp
  // viraria "100% da meta" numa conta que não vendeu nada.
  const hasGoal = goalCents !== null && goalCents > 0;
  return (
    <RevenueBar
      value={formatCents(receivedCents)}
      cents={{ value: receivedCents, format: (n) => formatCents(Math.round(n)) }}
      {...(hasGoal ? { goal: formatCents(goalCents), pct: (receivedCents / goalCents) * 100 } : {})}
    />
  );
}

function SellerShell() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const session = useQuery(sessionQueryOptions);
  const { mode } = useMode();
  useLiveEvents();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // `enabled`: sem sessão de seller a chamada só daria 401. Erro aqui não quebra o shell
  // (item 4 da task): `counters.data` fica `undefined` e a nav sobe sem badge, sem toast.
  const counters = useQuery({
    ...countersQueryOptions(mode),
    enabled: session.data?.role === 'seller_owner',
  });
  const profile = useQuery({
    ...profileQueryOptions(mode),
    enabled: session.data?.role === 'seller_owner',
  });
  // Menu de saque em USDT só pra quem tem saldo sacável (mesma query do saldo, cache compartilhado).
  const balance = useQuery({
    ...balanceQueryOptions(mode),
    enabled: session.data?.role === 'seller_owner',
  });
  const usdtWithdrawable =
    balance.data !== undefined && withdrawableAcquirers(balance.data.international).length > 0;
  const signOut = useMutation({
    mutationFn: async () => api('/v1/me/auth/sign-out', { method: 'POST' }),
    onSettled: () => {
      queryClient.clear();
      void navigate({ to: '/login' });
    },
  });
  const badges = counters.data === undefined ? undefined : sellerBadges(counters.data);

  if (session.isPending) {
    return (
      <div className="min-h-dvh p-4 sm:p-8">
        <CardSkeleton />
      </div>
    );
  }
  if (session.data === null || session.data === undefined) {
    return <Navigate to="/login" search={{ next: pathname }} replace />;
  }
  if (session.data.role !== 'seller_owner') {
    return <Navigate to="/no-access" replace />;
  }

  const user = session.data;

  return (
    <AppShell
      groups={filterNav(sellerNav, usdtWithdrawable)}
      activePath={pathname}
      badges={badges}
      user={{
        name: user.name,
        email: user.email,
        subtitle: profile.data?.display_name ?? user.email,
      }}
      accountLinks={ACCOUNT_LINKS}
      onLogout={() => {
        signOut.mutate();
      }}
      mobileTabs={MOBILE_TABS}
      modeToggle={<ModeToggle />}
      revenue={<MonthRevenue />}
      banner={
        mode === 'test' ? (
          <div className="flex items-center gap-2.5 rounded-lg bg-secondary px-3.5 py-2 text-12 text-muted-foreground">
            <span aria-hidden="true" className="size-1.5 shrink-0 bg-muted-foreground" />
            Modo de teste: nada aqui movimenta dinheiro de verdade.
          </div>
        ) : null
      }
    >
      <Outlet />
    </AppShell>
  );
}

export const Route = createFileRoute('/app')({ component: SellerShell });
